'use es6';

import { requiredTicketPropertiesForConversations } from 'forms-utils-lib/constants/Properties';
import { MetaDataProperties } from 'FormsUI/constants/FormMetaDataProperties';
import { getObjectTypeIdValue } from 'FormsCommonComponents/form-definition/utils/FormFieldUtils';
import { fetchHelpDesk } from 'FormsUI/api/Helpdesk';
import { fetchInboxes as fetchInboxesFromApi, fetchInboxByGuid, fetchInboxById } from 'FormsUI/api/Inboxes';
import { PropertyObjectTypesIds, PropertyObjectTypes } from 'ui-forms-components/constants/PropertyObjectTypes';
import { removeMetaData, updateMetaData } from 'FormsUI/actions/FormDefinition';
import { deleteFormField, appendFormFieldGroup } from 'FormsUI/actions/FormDragDrop';
import { showTransformToTicketDialog } from 'FormsUI/actions/DialogsAndPanels';
import { getFormGuid, getFormFieldGroups, getMetaDataValue } from 'FormsUI/selectors/FormDefinition';
import { getAllFieldsByPropertyId } from 'FormsUI/selectors/FormDefinition';
import { getInboxes, getCurrentOrFirstInboxId } from 'FormsUI/selectors/Inboxes';
import { isTicketProperty, getObjectTypeIdIdentifiersFromPropertyObjectTypeIdentifiers } from 'FormsCommonComponents/utils/PropertyObjectTypes';
import { addValidationErrorIfNeeded } from 'FormsUI/actions/FormDefinition';
import { TabValidationsKeys, ValidationKeys, ValidationType } from 'FormsUI/constants/FormFieldValidations';
import { isUngatedForHelpdesk as getIsUngatedForHelpdesk } from 'FormsUI/selectors/Gates';
import { hasHelpDeskAccess as getHasHelpDeskAccess } from 'FormsUI/selectors/Scope';
import { HELPDESK } from 'FormsUI/constants/WorkspaceFlags';
const createAction = name => `@@inboxes/${name}`;
export const FETCH_INBOXES_REQUEST = createAction('FETCH_INBOXES_REQUEST');
export const FETCH_INBOXES_RESPONSE = createAction('FETCH_INBOXES_RESPONSE');
export const FETCH_INBOXES_ERROR = createAction('FETCH_INBOXES_ERROR');
export const FETCH_CURRENT_INBOX_REQUEST = createAction('FETCH_CURRENT_INBOX_REQUEST');
export const FETCH_CURRENT_INBOX_RESPONSE = createAction('FETCH_CURRENT_INBOX_RESPONSE');
export const CLEAR_CURRENT_INBOX_RESPONSE = createAction('CLEAR_CURRENT_INBOX_RESPONSE');
export const FETCH_CURRENT_INBOX_ERROR = createAction('FETCH_CURRENT_INBOX_ERROR');
export const REQUEST_INBOXES = createAction('REQUEST_INBOXES');
export const REQUEST_INBOXES_SUCCESS = createAction('REQUEST_INBOXES_SUCCESS');
export const REQUEST_INBOXES_ERROR = createAction('REQUEST_INBOXES_ERROR');
export const TRY_TRANSFORM_OR_SHOW_DIALOG = createAction('TRY_TRANSFORM_OR_SHOW_DIALOG');
const fetchInboxesRequest = () => ({
  type: FETCH_INBOXES_REQUEST
});
const fetchInboxesResponse = ({
  inboxes
}) => ({
  type: FETCH_INBOXES_RESPONSE,
  inboxes
});
const fetchInboxesError = () => ({
  type: FETCH_INBOXES_ERROR
});
const fetchCurrentInboxRequest = () => ({
  type: FETCH_CURRENT_INBOX_REQUEST
});
const fetchCurrentInboxResponse = payload => ({
  type: FETCH_CURRENT_INBOX_RESPONSE,
  payload
});
const clearCurrentInboxResponse = () => ({
  type: CLEAR_CURRENT_INBOX_RESPONSE
});
const fetchCurrentInboxError = () => ({
  type: FETCH_CURRENT_INBOX_ERROR
});
const requestInboxes = () => ({
  type: REQUEST_INBOXES
});
const requestInboxesSuccess = inboxes => ({
  type: REQUEST_INBOXES_SUCCESS,
  inboxes
});
const requestInboxesError = error => ({
  type: REQUEST_INBOXES_ERROR,
  error
});
const tryTransformOrShowDialog = inboxId => ({
  type: TRY_TRANSFORM_OR_SHOW_DIALOG,
  inboxId
});

// FIXME: The BE currently does not filter out HD from the inboxes response, this can
// be removed once it does.
const removeHelpdeskFromInboxesResponse = response => {
  response.inboxes = response.inboxes.filter(inbox => inbox.workspaceFlag !== HELPDESK);
};
const fetchInboxesAction = dispatch => {
  return new Promise(resolve => {
    fetchInboxesFromApi().then(response => {
      removeHelpdeskFromInboxesResponse(response);
      dispatch(fetchInboxesResponse(response));
    }).catch(error => dispatch(fetchInboxesError(error))).finally(() => resolve());
  });
};
const fetchInboxesAndHelpdesk = dispatch => {
  const inboxesPromise = fetchInboxesFromApi();
  const helpdeskPromise = fetchHelpDesk();
  return Promise.all([inboxesPromise, helpdeskPromise]).then(([inboxesResponse, helpDeskResponse]) => {
    removeHelpdeskFromInboxesResponse(inboxesResponse);
    const allInboxesIncludingHelpdesk = [...inboxesResponse.inboxes, helpDeskResponse.inbox];
    const finalResponse = {
      inboxes: allInboxesIncludingHelpdesk
    };
    dispatch(fetchInboxesResponse(finalResponse));
  }).catch(error => dispatch(fetchInboxesError(error)));
};
export const fetchInboxes = () => (dispatch, getState) => {
  dispatch(fetchInboxesRequest());
  const isUngatedForHelpdesk = getIsUngatedForHelpdesk(getState());
  const hasHelpDeskAccess = getHasHelpDeskAccess(getState());
  if (isUngatedForHelpdesk && hasHelpDeskAccess) {
    fetchInboxesAndHelpdesk(dispatch);
  } else {
    fetchInboxesAction(dispatch);
  }
};
export const fetchCurrentInbox = () => (dispatch, getState) => {
  const formGuid = getFormGuid(getState());
  const currentInboxId = getMetaDataValue(getState(), MetaDataProperties.INBOX_ID);
  const fetchByFn = currentInboxId ? fetchInboxById.bind(null, currentInboxId) : fetchInboxByGuid.bind(null, formGuid);
  dispatch(fetchCurrentInboxRequest());
  return new Promise(resolve => {
    fetchByFn().then(response => dispatch(fetchCurrentInboxResponse(response))).catch(error => dispatch(fetchCurrentInboxError(error))).finally(() => resolve());
  });
};
export const undoTicketForm = () => {
  return (dispatch, getState) => {
    const isFieldTicket = field => !isTicketProperty(getObjectTypeIdValue(field.objectTypeId, field.propertyObjectType));
    const removeFields = variant => {
      const formFieldGroups = getFormFieldGroups(getState(), variant);
      const mappedFormFieldGroups = formFieldGroups.map(fieldGroup => Object.assign({}, fieldGroup, {
        fields: fieldGroup.fields.filter(field => isFieldTicket(field))
      }));
      const filteredAndMappedFormFieldGroups = mappedFormFieldGroups.filter(fieldGroup => fieldGroup.fields.length || fieldGroup.richText.type && fieldGroup.richText.content);
      dispatch(deleteFormField(filteredAndMappedFormFieldGroups, variant));
      if (!filteredAndMappedFormFieldGroups.length) {
        dispatch(addValidationErrorIfNeeded(TabValidationsKeys.FORM, ValidationKeys.NO_FIELDS, ValidationType.REQUIRED));
      }
    };
    removeFields();
    dispatch(removeMetaData(MetaDataProperties.INBOX_ID));
    dispatch(clearCurrentInboxResponse());
    dispatch(fetchCurrentInbox());
  };
};
export const transformToATicketForm = id => {
  return (dispatch, getState) => {
    const mapId = propertyName => `${PropertyObjectTypesIds[PropertyObjectTypes.TICKET]}/${propertyName}`;
    const fieldIdentifiers = getObjectTypeIdIdentifiersFromPropertyObjectTypeIdentifiers([...getAllFieldsByPropertyId(getState()).keys()]);
    const currentInbox = getInboxes(getState()).filter(({
      id: inId
    }) => inId === id)[0];
    dispatch({
      type: '@@inboxes/START_TRANSFORM_TO_A_TICKET_FORM',
      id
    });
    dispatch(updateMetaData(MetaDataProperties.INBOX_ID, id));
    dispatch(fetchCurrentInboxResponse(currentInbox));
    requiredTicketPropertiesForConversations.map(mapId).filter(fieldId => fieldIdentifiers.indexOf(fieldId) === -1).forEach(fieldId => {
      dispatch(appendFormFieldGroup(fieldId, {
        showConfigPanel: false
      }));
    });
  };
};
export const transformOrShowDialog = () => (dispatch, getState) => {
  const inboxId = getCurrentOrFirstInboxId(getState());
  dispatch(tryTransformOrShowDialog({
    inboxId
  }));
  if (inboxId) {
    return dispatch(transformToATicketForm(inboxId));
  } else {
    return dispatch(showTransformToTicketDialog());
  }
};
export const fetchInboxesAndSetInboxId = options => dispatch => {
  const setInboxId = options ? options.setInboxId : true;
  dispatch(requestInboxes());
  return Promise.all([dispatch(fetchCurrentInbox()), dispatch(fetchInboxes())]).then(() => {
    dispatch(requestInboxesSuccess());
    if (setInboxId) {
      dispatch(transformOrShowDialog());
    }
  }).catch(e => {
    dispatch(requestInboxesError(e));
  });
};